//@ts-nocheck
import React, { useRef } from 'react';
import DatePickerManager from './utils/DatePickerManager';
import useStateWithCallback from './hooks/useStateWithCallback';
import { getPropByString as get } from '../../helpers/props';
import type { DateObject } from './utils/DatePickerManager';
import useDevice from '@/hooks/useDevice';
import { adjustJalaliMonth, calculateBaseTimestamp, daysInMonth, formatJalaliDate, formatPersianDates, generateCurrentJalaliMonthDays, generateMonthDays, getCurrentDate, getTodayDate, mapWeekdayToPersian } from '@/helpers/dateManager';
type Props = {
  onChange: any;
  getCalendarFunc?: (fromDate: number, toDate: number) => void;
  dates: {
    checkin: number;
    checkout: number;
  };
  dateType: string;
  calendar?: any;
  checkinLabel?: string;
  checkoutLabel?: string;
  fieldToSet?: string;
  withoutStyle?: boolean;
  hasFooter?: boolean;
  isFull?: boolean;
  footerChildren?: any;
  headerChildren?: any;
  single?: boolean;
  endDay?: string | null;
  isUnitCount?: boolean;
  selectedDates?: any[];
};
type State = {
  hoveredDate: DateObject;
  pickerDate: DateObject;
  checkInDate?: any;
  todayDate: DateObject;
  checkOutDate?: any;
  months: any[];
  firstDisabledDay: DateObject;
  selectedDates: any[];
};

// A wrapper that contains all datepicker fucntions
// gets a datepicker component as an input
// and returns a complete datepicker
const DatePickerConverted = React.forwardRef((props: Props, ref) => {
  const {
    isMobile
  } = useDevice();
  const checkInDate = React.useRef(props.dates.checkin && !props.dates.checkout ? {
    ...getCurrentDate(new Date(props.dates.checkin * 1000)),
    ts: props.dates.checkin
  } : null);
  const oldCheckin = useRef(null);
  const checkOutDate = React.useRef(null);
  const hoveredDate = React.useRef(null);
  const firstDisabledDay = React.useRef(null);
  const [state, setState] = useStateWithCallback({
    firstDisabledDay: null,
    todayDate: getTodayDate(),
    nextRangeDays: DatePickerManager.nextRangeDays(props.nextRangeMonths),
    pickerDate: null,
    months: [],
    selectedDates: props.selectedDates || [],
    days: [],
    isLoading: false,
    hoveredDate: null
  });
  const isMounted = React.useRef(false);
  React.useImperativeHandle(ref, () => ({
    cleanValue,
    setCalendarDates,
    cleanCheckout,
    getThreeWeeksFromDate,
    state
  }));

  // While mount, checks for checkin and checkout props
  // to initialize datepicker values
  React.useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      if (props.dates.checkout || props.dates.checkin) {
        setCalendarDates(props.dates.checkin || null, props.dates.checkout || null);
      } else initializeCalendar(props.datePickerType);
    }
    //sd is for single dates
    if (props.type === 'sd') {
      initializeCalendar(props.datePickerType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dates.checkout, props.type, props.dates.checkin, props.calendar, props.datePickerType]);
  const setCalendarDates = (checkin: number, checkout: number) => {
    checkInDate.current = checkin ? {
      ...getCurrentDate(new Date(checkin * 1000)),
      ts: checkin
    } : null;
    checkOutDate.current = checkout ? {
      ...getCurrentDate(new Date(checkout * 1000)),
      ts: checkout
    } : null;
    state.hoveredDate = checkout ? {
      ...getCurrentDate(new Date(checkout * 1000)),
      ts: checkout
    } : null;
    initializeCalendar(props.datePickerType);
  };
  const cleanCheckout = () => {
    checkOutDate.current = null;
    initializeCalendar(props.datePickerType);
  };
  const initializeCalendar = (datePickerType: string) => {
    switch (datePickerType) {
      case 'SINGLE_RANGE':
        // setMonthsArrangement(1)
        calculateMonthsFromNowToNextRangeDays(0);
        break;
      case 'MULTI_ROW_RANGE':
        setMonthsArrangement(2);
        break;
      case 'MULTI_COLUMN_RANGE':
        setMonthsArrangement(2);
        break;
      case 'MULTI_COLUMN_RANGE_Scroll':
        calculateMonthsFromNowToNextRangeDays(props.nextRangeMonths);
        break;
      case 'DAYS_RANGE':
        getThreeWeeksFromDate(checkInDate.current);
        break;
      default:
    }
  };
  const applyReserveRules = (withStateUpdate = true) => {
    const data = DatePickerManager.applyReserveRules(state.months, props.calendar, checkInDate.current, checkOutDate.current, firstDisabledDay.current, oldCheckin.current, props.minDays);
    oldCheckin.current = data.checkedInDate;
    firstDisabledDay.current = data.firstDisabledDay;
    if (withStateUpdate) {
      setState(preState => ({
        ...preState,
        months: data.months
      }));
    }
  };
  // Handles day click - sets day as checkin or checkout
  const onDayClick = (selectedDate: DateObject) => {
    // if the date picker is single day type
    if (props.type === 'sd') {
      singleDaypicker(selectedDate);
    } else {
      rangeDatePicker(selectedDate);
    }
  };
  // Handles day hover and sets the recieved date as hovered
  const onDayHover = (date: DateObject) => {
    if ((checkInDate.current && !checkOutDate.current || !checkInDate.current && checkOutDate.current) && !isMobile) {
      const hoveredDate = date ? date : null;
      setState(state => ({
        ...state,
        hoveredDate
      }));
    }
  };
  const onRangePickerValueChange = () => {
    props.onChange({
      checkin: get(checkInDate.current, 'ts'),
      checkout: get(checkOutDate.current, 'ts')
    });
  };
  const onSingleDayPickerValueChange = ({
    ...newState
  }) => {
    props.onChange({
      selectedDates: newState.selectedDates
    });
  };
  const singleDaypicker = selectedDate => {
    const selectedDates = [...state.selectedDates];
    let index = null;
    selectedDates?.map((t, i) => {
      if (t.day === selectedDate.day && t.month === selectedDate.month && t.year === selectedDate.year) {
        index = i;
      }
    });
    if (state.todayDate.ts <= selectedDate.ts && !selectedDate.is_reserved) {
      index !== null ? selectedDates.splice(index, 1) : selectedDates.push(selectedDate);
      setState(preState => {
        return {
          ...preState,
          selectedDates
        };
      });
      onSingleDayPickerValueChange({
        ...state,
        selectedDates
      });
    }
  };
  // Cleans current values - function is accessible from parent using a ref
  const cleanValue = () => {
    cleanDatePickerValues();
  };
  const cleanDatePickerValues = () => {
    checkInDate.current = null;
    checkOutDate.current = null;
    setState(preState => ({
      ...preState,
      selectedDates: [],
      hoveredDate: null
    }));
    initializeCalendar(props.datePickerType);
  };

  // select ranged dates
  const rangeDatePicker = selectedDate => {
    if (!selectedDate.is_day_disabled && !selectedDate.isPassed) {
      const newState = {};
      if (props.fieldToSet) {
        const fieldToSet = props.fieldToSet === 'checkin' ? 'checkInDate' : 'checkOutDate';
        if (fieldToSet === 'checkInDate') {
          checkInDate.current = {
            ...selectedDate
          };
          if (checkOutDate.current && selectedDate.ts >= checkOutDate.current.ts) {
            checkOutDate.current = null;
          }
        } else {
          checkOutDate.current = selectedDate;
          if (checkInDate.current && selectedDate.ts <= checkInDate.current.ts) {
            checkOutDate.current = null;
            checkInDate.current = selectedDate;
          }
        }
        if (props.datePickerType !== 'DAYS_RANGE') {
          applyReserveRules(false);
          onRangePickerValueChange();
        } else {
          const convertedDays = createJalaliMonthArray(state.days);
          const resultArray = applyReserveRulesAndFlatten(convertedDays, checkInDate.current);
          setState(state => ({
            ...state,
            days: resultArray
          }));
          onRangePickerValueChange({
            days: resultArray
          });
        }
      } else {
        if (!checkInDate.current && !checkOutDate.current) {
          checkInDate.current = selectedDate;
        } else if (checkInDate.current && !checkOutDate.current && selectedDate.ts !== checkInDate.current.ts) {
          if (selectedDate.ts > checkInDate.current.ts) {
            checkOutDate.current = selectedDate;
            state.hoveredDate = selectedDate;
          } else {
            checkInDate.current = selectedDate;
          }
        } else if (selectedDate.ts === checkInDate.current.ts) {
          checkOutDate.current = null;
          checkInDate.current = null;
          state.hoveredDate = null;
        } else {
          checkOutDate.current = null;
          checkInDate.current = selectedDate;
          state.hoveredDate = null;
        }
        if (props.calendar) {
          if (props.datePickerType !== 'DAYS_RANGE') {
            applyReserveRules(false);
            onRangePickerValueChange();
          } else {
            const convertedDays = createJalaliMonthArray([...state.days]);
            const resultArray = applyReserveRulesAndFlatten(convertedDays, checkInDate.current, checkOutDate.current);
            setState(state => ({
              ...state,
              days: resultArray
            }));
            onRangePickerValueChange({
              days: resultArray
            });
          }
        } else {
          applyReserveRules();
          onRangePickerValueChange();
        }
      }
    }
  };

  // Creates and returns a month based on recieved calendar
  const createMonth = (newDate: any) => {
    const date = new Date(newDate);
    const weekDayString = date.toLocaleDateString('fa-IR', {
      weekday: 'long',
      month: 'numeric',
      numberingSystem: 'latn'
    });
    const currentYear = +date.toLocaleDateString('fa-IR', {
      year: 'numeric',
      numberingSystem: 'latn'
    });
    const currentMonth = +date.toLocaleDateString('fa-IR', {
      month: 'numeric',
      numberingSystem: 'latn'
    });
    // const type = props.dateType
    const month = {};
    month.days = [];
    const weekday = +weekDayString.split(' ')[0];
    const daysInMonths = daysInMonth(currentMonth, currentYear);
    month.firstDayOfMonthWeekDay = weekday % 7;
    month.month = +weekDayString.split(' ')[1];
    const monthData = generateMonthDays(currentMonth, currentYear);
    month.daysInMonth = daysInMonths;
    month.year = currentYear;
    month.ts = newDate;
    return monthData;
  };

  // Adds a new month to the current months list
  const onAddMonthClick = () => {
    const type = props.dateType; // Determine if the date type is Jalali or Gregorian

    const months = [...state.months]; // Copy the current months from the state
    const lastMonth = months[months.length - 1]; // Get the last month in the list

    let newMonth;
    if (type === 'JALALI') {
      // Handle Jalali dates
      const nextMonth = parseInt(lastMonth.month) % 12 + 1; // Calculate the next month (1-12)
      const nextYear = parseInt(lastMonth.year) + (nextMonth === 1 ? 1 : 0); // Increment the year if the month wraps around
      newMonth = generateCurrentJalaliMonthDays(new Date(calculateBaseTimestamp(nextYear, nextMonth) * 1000)); // Generate the Jalali month
    } else {
      // Handle Gregorian dates
      const lastMonthDate = new Date(lastMonth.year, lastMonth.month - 1, 1); // Create a `Date` object for the last month
      const nextMonthDate = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 1); // Increment the month
      newMonth = {
        month: nextMonthDate.getMonth() + 1,
        // Get the next month (1-based)
        year: nextMonthDate.getFullYear(),
        // Get the year
        days: [] // Placeholder for Gregorian days (populate if needed)
      };
    }
    months.push(createMonth(newMonth)); // Add the new month to the list

    setState(state => ({
      ...state,
      months
    }));
  };

  // Set month on next/1 or prev/0 month
  const onNextPrevMonthClick = async (isNext: boolean) => {
    const monthsToAdd = props.datePickerType === 'SINGLE_RANGE' ? 1 : 2;
    let months = [...state.months];
    if (props.calendar) {
      const monthsToGet = isNext ? state.months.length - 1 : 0;
      const lastMonth = {
        ...months[monthsToGet]
      };
      const startOfMonthTs = calculateBaseTimestamp(lastMonth.year, lastMonth.month);
      const dates = formatPersianDates(new Date(startOfMonthTs * 1000));
      if (props.getCalendarFunc) {
        const preTwo = adjustJalaliMonth(+dates.year, +dates.month, -2);
        const preTwoMonths = formatJalaliDate({
          ...preTwo,
          day: 1
        });
        // Add 12 months
        const next = adjustJalaliMonth(+dates.year, +dates.month, 10);
        const nextMonth = formatJalaliDate({
          ...next,
          day: 1
        });
        await props.getCalendarFunc(preTwoMonths, nextMonth);
      }
    }
    for (let i = 0; i < months.length; i++) {
      const {
        year,
        month
      } = months[i];
      const delta = isNext ? monthsToAdd : -monthsToAdd;
      const {
        year: newYear,
        month: newMonth
      } = adjustJalaliMonth(year, month, delta);
      const timestamp = calculateBaseTimestamp(newYear, newMonth);
      const adjustedDate = new Date(timestamp * 1000);
      months[i] = createMonth(adjustedDate);
      sessionStorage.setItem('calendar-date', adjustedDate.toString());
    }
    if (props.calendar) {
      const data = DatePickerManager.applyReserveRules(months, props.calendar, checkInDate.current, checkOutDate.current, firstDisabledDay.current, oldCheckin.current, props.minDays);
      oldCheckin.current = data.checkedInDate;
      firstDisabledDay.current = data.firstDisabledDay;
      months = data.months;
    }
    setState(state => ({
      ...state,
      months
    }));
  };
  const getMonthsInNextRangeDays = days => {
    // Get today's date at UTC start of day
    const now = new Date();
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    todayUTC.setUTCHours(0, 0, 0, 0);
    // Calculate future date after 'days'
    const futureDate = new Date(todayUTC.getTime());
    futureDate.setUTCDate(futureDate.getUTCDate() + days);

    // Use your DatePickerManager to convert Gregorian date to Jalali
    const todayJalali = getCurrentDate(todayUTC, 'JALALI');
    const futureJalali = getCurrentDate(futureDate, 'JALALI');

    // Calculate number of months difference
    const numberOfMonths = (futureJalali.year - todayJalali.year) * 12 + (futureJalali.month - todayJalali.month);
    return numberOfMonths + 1; // inclusive count of months
  };
  const setMonthsArrangement = numberOfMonths => {
    const type = props.dateType; // Either 'JALALI' or 'GREGORIAN'
    let months = [];
    let date = null;
    // Determine the initial month to display
    const hasSelectedMonth = checkInDate.current || state.selectedDates.length > 0 ? state.selectedDates[0] : null;
    if (hasSelectedMonth) {
      // If there's a selected check-in date or selected dates, start from that month
      const selectedTimestamp = hasSelectedMonth.ts * 1000; // Convert to milliseconds
      date = new Date(selectedTimestamp);
      date.setUTCDate(1); // Start of the month
    } else {
      // Otherwise, start from the current month
      date = new Date();
      date.setUTCDate(1); // Start of the current month
    }

    // Generate the months to display
    for (let i = 0; i < numberOfMonths; i++) {
      const prevMonths = sessionStorage.getItem('calendar-date');
      if (numberOfMonths === 1 && prevMonths) {
        const dateObj = new Date(prevMonths);
        months.push(createMonth(dateObj));
      } else {
        months.push(createMonth(date));

        // Move to the next month
        if (type === 'JALALI') {
          // For Jalali months (Persian calendar), we need a custom implementation
          date = getNextJalaliMonth(date);
        } else {
          // For Gregorian months
          date.setUTCMonth(date.getUTCMonth() + 1); // Increment the month
        }
      }
    }

    // Apply reservation rules if a calendar is provided
    if (props.calendar) {
      const data = DatePickerManager.applyReserveRules(months, props.calendar, checkInDate.current, checkOutDate.current, firstDisabledDay.current, oldCheckin.current, props.minDays);
      oldCheckin.current = data.checkedInDate;
      firstDisabledDay.current = data.firstDisabledDay;
      months = data.months;
    }

    // Update the state with the generated months
    setState(state => ({
      ...state,
      months
    }));
  };

  // Helper function to calculate the next month in the Jalali calendar
  function getNextJalaliMonth(currentDate) {
    const persianYear = currentDate.getUTCFullYear() - 622; // Approximate Jalali year
    const persianMonth = currentDate.getUTCMonth(); // Approximate Jalali month (0-based)

    let nextYear = persianYear;
    let nextMonth = persianMonth + 1;
    if (nextMonth > 11) {
      nextMonth = 0; // Reset to Farvardin (0-based)
      nextYear += 1; // Increment the year
    }

    // Convert back to Gregorian date for calculations
    const nextDate = new Date(Date.UTC(nextYear + 622, nextMonth, 1)); // Approximate Gregorian equivalent
    return nextDate;
  }
  const calculateMonthsFromNowToNextRangeDays = days => {
    const type = props.dateType; // Determine whether the date type is Jalali or Gregorian
    let months = [];

    // Get the current date or the start of the current month (Gregorian or Jalali)
    const now = new Date();
    const totalMonths = getMonthsInNextRangeDays(days); // Calculate the number of months in the range
    for (let i = 0; i < totalMonths; i++) {
      let currentMonthData;
      if (type === 'JALALI') {
        // Calculate the month offset dynamically for Jalali
        const {
          year,
          month
        } = formatPersianDates(now); // Get the current Jalali year and month
        const targetMonth = (parseInt(month) + i) % 12 || 12; // Handle month overflow
        const targetYear = parseInt(year) + Math.floor((parseInt(month) + i - 1) / 12); // Adjust year for overflow
        currentMonthData = generateCurrentJalaliMonthDays(new Date(calculateBaseTimestamp(targetYear, targetMonth) * 1000)); // Generate the Jalali month days
      } else {
        // Calculate the month offset dynamically for Gregorian
        const nextMonthDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1)); // Increment month
        currentMonthData = {
          month: nextMonthDate.getUTCMonth() + 1,
          year: nextMonthDate.getUTCFullYear(),
          days: [] // Placeholder for Gregorian days (can be populated later if needed)
        };
      }

      // const month = createMonth(currentMonthData) // Process the month data
      months.push(currentMonthData); // Add the month to the list
    }
    // Apply reservation rules if a calendar is provided
    if (props.calendar) {
      const data = DatePickerManager.applyReserveRules(months, props.calendar, checkInDate.current, checkOutDate.current, firstDisabledDay.current, oldCheckin.current, props.minDays);
      oldCheckin.current = data.checkedInDate;
      firstDisabledDay.current = data.firstDisabledDay;
      months = data.months;
    }
    setState(state => ({
      ...state,
      months
    }));
    // Update the state with the calculated months
    return months;
  };
  const getThreeWeeksFromDate = () => {
    const startOfWeek = getStartOfWeek(checkInDate.current);
    const daysArray = generateDaysArray(startOfWeek);
    const convertedDays = createJalaliMonthArray(daysArray);
    const resultArray = applyReserveRulesAndFlatten(convertedDays);
    setState(state => ({
      ...state,
      days: resultArray
    }));
    return resultArray;
  };
  const getStartOfWeek = checkInDate => {
    // If checkInDate is passed, get the Date object from its ts (assumed in seconds)
    const date = checkInDate ? new Date(checkInDate.ts * 1000) : new Date();

    // Convert to UTC midnight of that day
    const utcYear = date.getUTCFullYear();
    const utcMonth = date.getUTCMonth();
    const utcDate = date.getUTCDate();
    const utcMidnight = new Date(Date.UTC(utcYear, utcMonth, utcDate));

    // Get day of week in UTC (0=Sunday, ... 6=Saturday)
    const dayOfWeek = utcMidnight.getUTCDay();

    // Calculate how many days to subtract to get last Saturday
    // Saturday is 6, so days since Saturday = (dayOfWeek + 1) % 7
    const daysSinceSaturday = (dayOfWeek + 1) % 7;

    // Calculate the start of the week date (Saturday midnight UTC)
    utcMidnight.setUTCDate(utcMidnight.getUTCDate() - daysSinceSaturday);

    // Return Unix timestamp in seconds
    return Math.floor(utcMidnight.getTime() / 1000);
  };
  const generateDaysArray = startOfWeek => {
    const daysArray = [];
    const date = new Date(startOfWeek * 1000);
    for (let i = 0; i < 21; i++) {
      // Clone date by creating a new Date object from the timestamp
      const day = new Date(date.getTime());
      // Add i days
      day.setUTCDate(day.getUTCDate() + i);

      // Format the day using your DatePickerManager's format function
      const formattedDay = formatDay(day);
      daysArray.push(formattedDay);
    }
    return daysArray;
  };
  const formatDay = date => {
    // Format Persian date parts
    const {
      year,
      month,
      day
    } = formatPersianDates(date);

    // Get weekday number (0=Sunday .. 6=Saturday) in UTC
    const weekDayNumber = date.getUTCDay();

    // Map weekday number to Persian weekday name
    const weekDay = mapWeekdayToPersian(weekDayNumber);
    return {
      day: Number(day),
      month: Number(month),
      ts: Math.floor(date.getTime() / 1000),
      weekDay,
      year: Number(year)
    };
  };
  const applyReserveRulesAndFlatten = (daysArray, checkin = checkInDate.current, checkout = checkOutDate.current) => {
    const data = props.calendar ? DatePickerManager.applyReserveRules(daysArray, props.calendar, checkin, checkout, firstDisabledDay.current, oldCheckin.current, props.minDays) : {};
    oldCheckin.current = data?.checkedInDate;
    firstDisabledDay.current = data?.firstDisabledDay;
    const months = data?.months || [];
    const resultArray = months.flatMap(obj => Array.isArray(obj.days) ? obj.days : []);
    return resultArray;
  };
  const createJalaliMonthArray = jalaliDatesArray => {
    const jalaliMonthMap = {};

    // Iterate over each date in the array
    for (const date of jalaliDatesArray) {
      const {
        year,
        month,
        day,
        ts,
        weekDay
      } = date;
      const yearMonthKey = `${year}-${month}`;

      // Create month object if it doesn't exist
      if (!jalaliMonthMap[yearMonthKey]) {
        jalaliMonthMap[yearMonthKey] = {
          yearMonth: yearMonthKey,
          days: [],
          daysInMonth: 0,
          firstDayOfMonthWeekDay: 0,
          month,
          ts: 0,
          year,
          dayObject: {}
        };
      }

      // Add day information to the month object
      const dayInfo = {
        day,
        ts,
        month,
        weekDay,
        year
      };
      jalaliMonthMap[yearMonthKey].dayObject[day] = dayInfo;
      jalaliMonthMap[yearMonthKey].days.push(dayInfo);
    }

    // Convert the month map to an array and calculate additional properties
    const jalaliMonthArray = Object.values(jalaliMonthMap);
    for (const monthObject of jalaliMonthArray) {
      const daysInMonth = monthObject.days.length;
      const firstDayOfMonthWeekDay = new Date(`${monthObject.year}-${monthObject.month}-01`).getDay();
      const ts = Math.round(new Date(`${monthObject.year}-${monthObject.month}-01`).getTime() / 1000);
      monthObject.daysInMonth = daysInMonth;
      monthObject.firstDayOfMonthWeekDay = firstDayOfMonthWeekDay;
      monthObject.ts = ts;
    }
    return jalaliMonthArray;
  };
  const convertArrayToDaysObject = (daysArray, year, month) => {
    const daysObject = {
      days: [...daysArray],
      daysInMonth: daysArray.length,
      firstDayOfMonthWeekDay: new Date(`${year}-${month}-01`).getDay(),
      month,
      ts: Math.round(new Date(`${year}-${month}-01`).getTime() / 1000),
      year
    };
    return daysObject;
  };
  return <>
      <props.WrappedDatePicker dateType={props.dateType} checkInDate={checkInDate.current} checkOutDate={checkOutDate.current} ref={ref} months={state.months} onAddMonthClick={onAddMonthClick} hoveredDate={state.hoveredDate} onDayClick={onDayClick} onDayHover={onDayHover} todayDate={state.todayDate} nextRangeDays={state.nextRangeDays} onNextPrevMonthClick={onNextPrevMonthClick} calendar={props.calendar} checkinLabel={props.checkinLabel} checkoutLabel={props.checkoutLabel} selectedDates={state.selectedDates} updateCalendarState={setState} withoutStyle={props.withoutStyle} dayType={props.dayType} isFull={props.isFull} footerChildren={props.footerChildren} headerChildren={props.headerChildren} days={state.days} endDay={props.endDay} isLoading={state.isLoading} isUnitCount={props.isUnitCount} withTooltip={props.withTooltip} />
    </>;
});
DatePickerConverted.defaultProps = {
  dates: {
    checkin: null,
    checkout: null
  },
  dateType: 'JALALI',
  hasCalendar: false,
  nextRangeMonths: 90,
  withTooltip: true
};
DatePickerConverted.displayName = 'DatePickerConverted';
export default React.memo(DatePickerConverted);